/**
 * Access-token lifecycle.
 *
 * The access token lives in memory only — never in `localStorage`, where any
 * XSS could read it. The refresh token is an httpOnly cookie the JavaScript
 * cannot touch at all; the browser attaches it automatically to the refresh
 * call, which is the only endpoint that accepts it.
 *
 * Refresh is single-flight: a gallery page can fire twenty requests at once as
 * a token expires, and all twenty must wait on one refresh rather than starting
 * twenty rotations — which, with reuse detection on the server, would look like
 * a stolen token and log the user out of everything.
 */

const REFRESH_MARGIN_MS = 60_000;

interface Session {
  accessToken: string;
  expiresAt: number;
}

let session: Session | null = null;
let inFlight: Promise<boolean> | null = null;
let onExpired: (() => void) | null = null;

export function setSession(
  accessToken: string,
  expiresInSeconds: number,
): void {
  session = { accessToken, expiresAt: Date.now() + expiresInSeconds * 1000 };
}

export function clearSession(): void {
  session = null;
}

export function getAccessToken(): string | null {
  return session?.accessToken ?? null;
}

export function hasSession(): boolean {
  return session !== null;
}

/** Called when refresh fails, so the app can route back to sign-in. */
export function onSessionExpired(handler: () => void): void {
  onExpired = handler;
}

function isStale(): boolean {
  if (!session) return true;
  return session.expiresAt - REFRESH_MARGIN_MS <= Date.now();
}

/**
 * Exchange the refresh cookie for a new access token.
 *
 * `credentials: "include"` is what sends the cookie; the custom header is what
 * a cross-origin form post cannot forge, and is the CSRF control.
 */
async function refresh(apiUrl: string): Promise<boolean> {
  try {
    const response = await fetch(`${apiUrl}/trpc/auth.refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json", "x-fc-client": "web" },
      body: JSON.stringify({}),
    });

    if (!response.ok) return false;

    const body = (await response.json()) as {
      result?: { data?: { accessToken: string; expiresIn: number } };
    };
    const data = body.result?.data;
    if (!data?.accessToken) return false;

    setSession(data.accessToken, data.expiresIn);
    return true;
  } catch {
    return false;
  }
}

/** Single-flight wrapper: concurrent callers share one rotation. */
export function refreshSession(apiUrl: string): Promise<boolean> {
  inFlight ??= refresh(apiUrl).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/**
 * Guarantee a usable token before a request goes out. Returns false when the
 * user needs to sign in again.
 */
export async function ensureFreshSession(apiUrl: string): Promise<boolean> {
  if (!isStale()) return true;

  const refreshed = await refreshSession(apiUrl);
  if (!refreshed) {
    clearSession();
    onExpired?.();
  }
  return refreshed;
}

export function notifyExpired(): void {
  clearSession();
  onExpired?.();
}
